import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraphType } from "../Data/GraphTypeSelector";
import { GraphTypeSelector } from "../Data/GraphTypeSelector";
import { useContext } from "react";
import AuthContext from "@/store/contexts/AuthContext";
import SelectWithSearch from "@/components/ui/select-search";
import { useGraphConfig } from "@/store/contexts/GraphConfigContext";
import InputTitleDescription from "../common/InputTitleDescription";
import SimpleSelect from "@/components/ui/simple-select";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "react-i18next";

type BasicInfoProps = {
  configType: "graph" | "table" | "block";
};

const BasicInfo = ({ configType }: BasicInfoProps) => {
  const { fields } = useGraphConfig();
  const { config, setConfig } = useContext(GraphConfigFormContext);
  const { t } = useTranslation();

  if (!config) return null;

  // Handle title change
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setConfig({
      ...config,
      title: newTitle,
    });
  };

  // Handle chart type change
  const handleChartTypeChange = (newType: GraphType) => {
    setConfig({
      ...config,
      graph_options: {
        ...config.graph_options,
        chartType: newType,
      },
    });
  };

  const aggregationTypes = [
    { label: t("editor.graphConfig.data.aggregationTypes.sum"), value: "sum" },
    { label: t("editor.graphConfig.data.aggregationTypes.avg"), value: "avg" },
    {
      label: t("editor.graphConfig.data.aggregationTypes.count"),
      value: "count",
    },
    {
      label: t("editor.graphConfig.data.aggregationTypes.count_distinct"),
      value: "count_distinct",
    },
    { label: t("editor.graphConfig.data.aggregationTypes.min"), value: "min" },
    { label: t("editor.graphConfig.data.aggregationTypes.max"), value: "max" },
  ];

  return (
    <>
      <div className="flex flex-col gap-2">
        <Label>{t("editor.graphConfig.data.title")}</Label>
        <Input
          type="text"
          placeholder={
            configType === "graph"
              ? t("editor.graphConfig.data.unNamedGraph")
              : configType === "table"
              ? t("editor.graphConfig.data.unNamedTable")
              : t("editor.graphConfig.data.unNamedBlock")
          }
          value={config.title}
          onChange={handleTitleChange}
        />
      </div>

      {configType === "block" && (
        <div className="flex flex-row w-full items-center justify-between">
          <div className="flex flex-col gap-1">
            <Label htmlFor="only_count" className="flex flex-col items-start">
              <span>{t("editor.graphConfig.data.onlyCount")}</span>
              <p className="text-xs text-muted-foreground">
                {t("editor.graphConfig.data.onlyCountDescription")}
              </p>
            </Label>
          </div>
          <Switch
            id="only_count"
            checked={config.block_options.only_count}
            onCheckedChange={(checked) =>
              setConfig({
                ...config,
                block_options: {
                  ...config.block_options,
                  only_count: checked,
                },
              })
            }
          />
        </div>
      )}

      {configType === "graph" && (
        <div className="flex flex-col gap-2 mt-2">
          <Label>{t("editor.graphConfig.data.chartType")}</Label>
          <GraphTypeSelector
            value={config.graph_options.chartType as GraphType}
            onChange={handleChartTypeChange}
          />
        </div>
      )}
      {configType === "block" && !config.block_options.only_count && (
        <>
          <div className="flex flex-col gap-2 my-2">
            <Label>{t("editor.graphConfig.data.fieldUsed")}</Label>
            <SelectWithSearch
              value={config.block_options?.field}
              options={fields}
              placeholder={t("editor.graphConfig.data.selectFieldPlaceholder")}
              onChange={(value) =>
                setConfig({
                  ...config,
                  block_options: {
                    ...config.block_options,
                    field: value,
                  },
                })
              }
            />
          </div>
          <InputTitleDescription
            title={t("editor.graphConfig.data.aggregation")}
            description={t("editor.graphConfig.data.aggregation")}
          >
            <SimpleSelect
              className="w-2/5"
              value={config.block_options.aggregation}
              options={aggregationTypes}
              onChange={(value) =>
                setConfig({
                  ...config,
                  block_options: {
                    ...config.block_options,
                    aggregation: value as "sum",
                  },
                })
              }
            />
          </InputTitleDescription>
        </>
      )}
    </>
  );
};

export default BasicInfo;
